import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.css']
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input() length: number = 0;
  @Input() pageSize: number = 10;
  @Input() pageIndex: number = 0;
  @Input() pageSizeOptions: number[] = [10, 20, 50, 100];
  @Input() showFirstLastButtons: boolean = true;
  @Input() simplePagination: boolean = false;

  @Input() moduleKey?: string;

  @Output() page = new EventEmitter<PageEvent>();

  pages: number[] = [];
  selectedPageNumber: number = 1;
  totalPages: number = 1;

  get pageLabel(): string {
    const lang = localStorage.getItem('lang') || 'en-US';
    const dictPage: Record<string, string> = {
      'en-US': 'Page',
      'hi-IN': 'पेज',
      'mr-IN': 'पेज',
      'bn-IN': 'পেজ'
    };
    return dictPage[lang] || 'Page';
  }

  ngOnInit(): void {
    this.checkAndApplySavedPageSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.checkAndApplySavedPageSize();
    this.recalculatePages();
  }

  private getStorageKey(): string {
    const key = this.moduleKey || this.getCleanPath();
    return key ? `app_page_size_${key}` : 'app_page_size';
  }

  private getCleanPath(): string {
    const path = window.location.pathname || '';
    const excludedKeywords = ['list', 'search', 'filter', 'edit', 'new', 'crud', 'details', 'view'];
    const segments = path.split('/')
      .filter(segment => {
        if (!segment) return false;
        if (excludedKeywords.includes(segment.toLowerCase())) return false;
        const isDynamicId = /^\d+$/.test(segment) || /^[a-f0-9-]{20,}$/.test(segment);
        if (isDynamicId) return false;
        return true;
      });
    return segments.join('_') || 'default';
  }

  private checkAndApplySavedPageSize(): void {
    const storageKey = this.getStorageKey();
    const savedSize = localStorage.getItem(storageKey) || sessionStorage.getItem(storageKey);
    if (savedSize) {
      const parsedSize = parseInt(savedSize, 10);
      if (parsedSize > 0 && this.pageSizeOptions.includes(parsedSize) && parsedSize !== this.pageSize) {
        this.pageSize = parsedSize;
        this.page.emit({
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          length: this.length,
          previousPageIndex: this.pageIndex
        });
      }
    }
  }

  private recalculatePages(): void {
    const validPageSize = this.pageSize > 0 ? this.pageSize : 10;
    this.totalPages = Math.ceil(this.length / validPageSize) || 1;

    // Guard against pageIndex out of bounds
    if (this.pageIndex >= this.totalPages && this.totalPages > 0) {
      const prevPageIndex = this.pageIndex;
      this.pageIndex = Math.max(0, this.totalPages - 1);
      if (prevPageIndex !== this.pageIndex) {
        this.page.emit({
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          length: this.length,
          previousPageIndex: prevPageIndex
        });
      }
    }

    this.pages = [];
    if (!this.simplePagination) {
      for (let i = 1; i <= this.totalPages; i++) {
        this.pages.push(i);
      }
    }

    this.selectedPageNumber = this.pageIndex + 1;
  }

  onPageSelectChange(value: any): void {
    const pageNum = Number(value);
    if (isNaN(pageNum) || pageNum < 1 || pageNum > this.totalPages) {
      return;
    }

    const newPageIndex = pageNum - 1;
    if (newPageIndex !== this.pageIndex) {
      const previousPageIndex = this.pageIndex;
      this.pageIndex = newPageIndex;
      this.selectedPageNumber = pageNum;

      this.page.emit({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        length: this.length,
        previousPageIndex: previousPageIndex
      });
    }
  }

  onPaginatorChange(event: PageEvent): void {
    const previousPageIndex = this.pageIndex;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.selectedPageNumber = event.pageIndex + 1;

    if (event.pageSize) {
      const storageKey = this.getStorageKey();
      localStorage.setItem(storageKey, String(event.pageSize));
      sessionStorage.setItem(storageKey, String(event.pageSize));
      localStorage.setItem('app_page_size', String(event.pageSize));
      sessionStorage.setItem('app_page_size', String(event.pageSize));
    }

    this.recalculatePages();

    this.page.emit({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      length: event.length,
      previousPageIndex: previousPageIndex
    });
  }
}
